import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fmtMXN, STATUS_LABEL } from '@/lib/format';
import { toast } from 'sonner';
import { usePeriodSummary } from '@/hooks/usePeriodSummary';
import PeriodPicker from '@/components/PeriodPicker';
import { api, apiErr } from '@/lib/api';
import { Lock, Unlock } from 'lucide-react';

const Row = ({ label, value, tid, danger }) => (
  <div className="flex items-center justify-between py-2 border-b border-border last:border-0" data-testid={tid}>
    <span className="text-sm">{label}</span>
    <span className={`font-display font-bold num text-sm ${danger ? 'text-destructive' : ''}`}>{value}</span>
  </div>
);

export default function Conciliation() {
  const { data, loading, refresh } = usePeriodSummary();
  if (loading || !data) return <div className="pt-8 text-center text-muted-foreground">Cargando…</div>;
  const { period, summary } = data;

  const setStatus = async (status) => {
    try { await api.patch(`/periods/${period.id}`, { status }); toast.success('Estado actualizado'); refresh(); } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleLock = async () => {
    try {
      if (period.locked) { const r = window.prompt('Motivo para reabrir el periodo:'); if (r === null) return; await api.post(`/periods/${period.id}/unlock?reason=${encodeURIComponent(r)}`); toast.success('Periodo reabierto'); }
      else { if (!window.confirm('¿Bloquear este mes?')) return; await api.post(`/periods/${period.id}/lock`); toast.success('Bloqueado'); }
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PeriodPicker/>
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight">Conciliación</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Ventas · CFDI · Depósitos</p>
      </div>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Ventas vs facturación</p>
        <Row tid="rec-ventas-totales" label="Ventas totales" value={fmtMXN(summary.ingresos_totales)}/>
        <Row tid="rec-facturadas" label="Facturas individuales" value={fmtMXN(summary.total_facturadas)}/>
        <Row tid="rec-cfdi" label="CFDI global" value={fmtMXN(summary.total_cfdi_global)}/>
        <Row tid="rec-diff-facturacion" label="Diferencia" value={fmtMXN(summary.diferencia_facturacion)} danger={Math.abs(summary.diferencia_facturacion) > 0.5}/>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Ventas vs cobros</p>
        <Row tid="rec-depositos" label="Depósitos bancarios" value={fmtMXN(summary.total_depositos)}/>
        <Row tid="rec-efectivo" label="Efectivo pendiente" value={fmtMXN(summary.efectivo_pendiente)}/>
        <Row tid="rec-diff-cobros" label="Diferencia contra ventas" value={fmtMXN(summary.diferencia_deposito)} danger={Math.abs(summary.diferencia_deposito) > 0.5}/>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Estado del periodo</p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <Button key={k} data-testid={`status-${k}`} variant={period.status === k ? 'default' : 'outline'} className="h-10 rounded-full text-xs" onClick={() => setStatus(k)}>
              {v}
            </Button>
          ))}
        </div>
        <Button data-testid="btn-toggle-lock" variant="outline" onClick={toggleLock} className="w-full mt-4 h-12 rounded-full">
          {period.locked ? <><Unlock className="h-4 w-4 mr-2"/> Reabrir mes</> : <><Lock className="h-4 w-4 mr-2"/> Bloquear mes</>}
        </Button>
      </Card>
    </div>
  );
}
